// @ts-strict-ignore
import { ChangeDetectionStrategy, Component, effect, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ModalController, ViewWillLeave } from "@ionic/angular";
import { Edge, Service, Websocket } from "src/app/shared/shared";
import { Pagination } from "../shared/service/pagination";
import { RouteService } from "../shared/service/route.service";
import { UserService } from "../shared/service/user.service";

/** This component is needed as a routing parent and acts as a transit station without being displayed. */
@Component({
    selector: "edge",
    template: `
        <ion-content></ion-content>
        <ion-router-outlet id="content"></ion-router-outlet>
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EdgeComponent implements OnInit, OnDestroy, ViewWillLeave {
    protected latestIncident: { message: string | null; id: string } | null = null;
    private edge: Edge | null = null;

    constructor(
        private routeService: RouteService,
        private service: Service,
        private websocket: Websocket,
        private pagination: Pagination,
        private popoverCtrl: ModalController,
        private router: Router,
        private userService: UserService,
    ) {
        effect(async () => {
            const edge = this.service.currentEdge();
            const edgeId = this.routeService.getRouteParam<string>("edgeId");
            if (!edgeId || !edge || edge.id == this.edge?.id) {
                return;
            }
            this.edge = edge;

            pagination.getAndSubscribeEdge(edge.id);
        });
    }

    /**
     * ROOT CAUSE (gefunden 2026-08-11 per Live-Reproduktion, siehe
     * [[project_excello_oems_sonnefeld1_ui_cold_load_bug]]): Bei einem
     * harten Reload/Direktaufruf einer Edge-Seite feuert Ionics
     * `ionViewWillEnter()` NICHT zuverlässig bei der allerersten Aktivierung
     * eines `ion-router-outlet` (bestaetigt per Konsolen-Log: kein einziger
     * "ionViewWillEnter"-Log-Eintrag, obwohl LiveComponent bereits lief und
     * `currentEdge()` dauerhaft null blieb). `ionViewWillEnter` ist an
     * Ionics Page-Transition-Stack gebunden - nur bei NACHFOLGENDEN
     * Navigationen innerhalb desselben Outlets (z.B. Tab-Wechsel, exakt das
     * bekannte "Tab-Wechsel hilft"-Workaround) feuert es zuverlässig.
     * `ngOnInit()` dagegen feuert garantiert genau einmal bei der
     * Komponenten-Erstellung, unabhängig von Ionics Page-Lifecycle - das ist
     * daher der zuverlässige Ort fuer den initialen Edge-Load.
     * `enterEdgeView()` wird bewusst aus BEIDEN Hooks aufgerufen: `ngOnInit`
     * deckt den Cold-Load ab, `ionViewWillEnter` bleibt fuer echte
     * Tab-Wechsel/Wiedereintritt in eine bereits bestehende Komponenten-
     * Instanz zustaendig. Beide Aufrufe sind gefahrlos redundant -
     * `Service.updateCurrentEdge()` hat einen Cache-Fastpath,
     * `Pagination.subscribeEdge()` einen `States.isAtLeast(...)`-Guard.
     */
    async ngOnInit() {
        // eslint-disable-next-line no-console
        console.log("[cold-load-debug] EdgeComponent.ngOnInit: fired");
        await this.enterEdgeView();
    }

    async ionViewWillEnter() {
        // eslint-disable-next-line no-console
        console.log("[cold-load-debug] EdgeComponent.ionViewWillEnter: fired");
        await this.enterEdgeView();
    }

    private async enterEdgeView() {
        const edgeId = this.routeService.getRouteParam<string>("edgeId");
        // Diagnose fuer den Cold-Load-Bug, siehe
        // [[project_excello_oems_sonnefeld1_ui_cold_load_bug]] - bewusst
        // NICHT an debugMode gebunden, damit es auch im Prod-Build beim
        // Reproduzieren sichtbar ist. Diese Zeilen sind ein Diagnose-Schritt,
        // kein dauerhaftes Feature - nach Verifikation des Fixes wieder entfernen.
        // eslint-disable-next-line no-console
        console.log("[cold-load-debug] EdgeComponent.enterEdgeView: start, edgeId=", edgeId);
        await this.service.updateCurrentEdge(edgeId);
        // eslint-disable-next-line no-console
        console.log("[cold-load-debug] EdgeComponent.enterEdgeView: updateCurrentEdge resolved");
        const edge = this.service.currentEdge();
        // eslint-disable-next-line no-console
        console.log("[cold-load-debug] EdgeComponent.enterEdgeView: currentEdge() =", edge?.id ?? null);
        await this.pagination.subscribeEdge(edge, this.websocket);
        // eslint-disable-next-line no-console
        console.log("[cold-load-debug] EdgeComponent.enterEdgeView: subscribeEdge resolved");
    }

    public ionViewWillLeave() {
        this.ngOnDestroy();
    }

    public ngOnDestroy(): void {
        this.service.currentEdge.set(null);
        if (!this.edge) {
            return;
        }
    }
}
